import { useAuth0 } from "@auth0/auth0-react";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import Lottie from "lottie-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import waitingAnimation from "../assets/lottie/waiting_lottie.json";
import { getClientId } from "../config/domain";
import { useRequest } from "../context/RequestContext";
import { useUploadContext } from "../context/UploadContext";

// Import engine functions
import {
  generateDOCX as generateDOCXEngine,
  proceedWithDOCXGeneration,
} from "../engine/docxEngine";
import {
  ensureProperNewlines,
  formatBoldText,
  hasPlaceholders,
} from "../engine/markdownEngine";
import {
  generatePDF as generatePDFEngine,
  proceedToPDFGeneration,
} from "../engine/pdfEngine";

const parseAmount = (amount) => {
  return Number((amount || "0").split(" ")[0].replace(",", ""));
};

const replaceRemainingOrders = (processedTemplate, largestOrder) => {
  // Look for remaining "order" values that haven't been replaced yet
  const orderRegex = /\borderTitle\b/g;
  let tempTemplate = processedTemplate;
  let nextOrder = largestOrder + 1;

  // Replace any remaining standalone "order" placeholders with consecutive numbers
  let orderCount = 0;
  while (orderRegex.test(tempTemplate)) {
    orderCount++;
    // Reset regex lastIndex for next search
    orderRegex.lastIndex = 0;

    // Replace only the first occurrence in each iteration
    tempTemplate = tempTemplate.replace(orderRegex, String(nextOrder));
    processedTemplate = processedTemplate.replace(
      orderRegex,
      String(nextOrder)
    );

    console.log(`Replaced 'order' with '${nextOrder}'`);
    nextOrder++;

    // Safety check to prevent infinite loops
    if (orderCount > 100) {
      console.warn(`Too many order replacements, stopping at ${orderCount}`);
      break;
    }
  }

  if (orderCount > 0) {
    console.log(`Replaced ${orderCount} 'order' placeholders consecutively`);
  }
  return processedTemplate;
};

const replaceTitleAndResult = (
  processedTemplate,
  documentOrderIds,
  targetIds,
  data,
  placeholder,
  documentData
) => {
  const field = placeholder.includes("title") ? "title" : "result";
  if (field === "title") {
    documentOrderIds = documentOrderIds.filter(
      (id) => id !== "MergeRequest"
      // && !id.includes('SingleTop'),
    );
  }
  const targetTitles = documentOrderIds
    .map((id, order) => {
      return { id, order };
    })
    .filter(({ id, order }) => targetIds.includes(id));
  console.log("mytitles", targetTitles);
  if (targetTitles.length > 0 && data.titles) {
    // Prepare titles content - join each title with a newline (KEEPING EXISTING LOGIC)
    const titlesContent = targetTitles
      .map(({ id, order }) => {
        if (data.titles[id] && data.titles[id][field]) {
          let content = data.titles[id][field].replace("order", order + 1);
          // Apply replacements to the title/result content
          const allReplacementKeys = { ...documentData };

          // Handle damage payments replacement in titles/results
          if (
            documentData.damagePayments &&
            Object.keys(documentData.damagePayments).length > 0
          ) {
            const damagePaymentsText = Object.entries(
              documentData.damagePayments
            )
              .map(([date, amounts]) => {
                amounts = amounts.map((amount) => `${amount} TL`);
                const formattedAmounts = amounts.join(", ");
                return `${date} tarihinde ${formattedAmounts}`;
              })
              .join(" ve ");

            // Replace damage payment placeholders in title/result content
            content = content.replace(
              /damagePaymentDate tarihinde damagePaymentAmount/g,
              damagePaymentsText
            );
            content = content.replace(
              /_damagePaymentDate_ tarihinde _damagePaymentAmount_/g,
              damagePaymentsText
            );
            content = content.replace(
              /\*damagePaymentDate\* tarihinde \*damagePaymentAmount\*/g,
              damagePaymentsText
            );
          }

          // Handle third party payments replacement in titles/results
          if (
            documentData.thirdPartyPayments &&
            Object.keys(documentData.thirdPartyPayments).length > 0
          ) {
            const thirdPartyPaymentsText = Object.entries(
              documentData.thirdPartyPayments
            )
              .map(([plate, dateAmounts]) => {
                const dateEntries = Object.entries(dateAmounts)
                  .map(([date, amounts]) => {
                    const formattedAmounts = amounts
                      .map((amount) => `${amount} TL`)
                      .join(", ");
                    return `${date} tarihinde ${formattedAmounts}`;
                  })
                  .join(" ve ");
                return `${plate} plakalı araca ${dateEntries}`;
              })
              .join(" ve ");

            // Replace third party payment placeholders in title/result content - new format
            content = content.replace(
              /\*thirdPartyPlate\* plakalı araca \*thirdPartyPaymentDate\* tarihinde \*thirdPartyPaymentAmount\*/g,
              thirdPartyPaymentsText
            );
            content = content.replace(
              /_thirdPartyPlate_ plakalı araca _thirdPartyPaymentDate_ tarihinde _thirdPartyPaymentAmount_/g,
              thirdPartyPaymentsText
            );
            content = content.replace(
              /thirdPartyPlate plakalı araca thirdPartyPaymentDate tarihinde thirdPartyPaymentAmount/g,
              thirdPartyPaymentsText
            );

            // Keep old format for backward compatibility
            content = content.replace(
              /thirdPartyPaymentDate tarihinde thirdPartyPaymentAmount/g,
              thirdPartyPaymentsText
            );
            content = content.replace(
              /_thirdPartyPaymentDate_ tarihinde _thirdPartyPaymentAmount_/g,
              thirdPartyPaymentsText
            );
            content = content.replace(
              /\*thirdPartyPaymentDate\* tarihinde \*thirdPartyPaymentAmount\*/g,
              thirdPartyPaymentsText
            );
          }

          // Handle depreciation payments replacement in titles/results
          if (
            documentData.depreciationPayments &&
            Object.keys(documentData.depreciationPayments).length > 0
          ) {
            const depreciationPaymentsText = Object.entries(
              documentData.depreciationPayments
            )
              .map(([date, amounts]) => {
                amounts = amounts.map((amount) => `${amount} TL`);
                const formattedAmounts = amounts.join(", ");
                return `${date} tarihinde ${formattedAmounts}`;
              })
              .join(" ve ");

            // Replace depreciation payment placeholders in title/result content
            content = content.replace(
              /depreciationPaymentDate tarihinde depreciationPaymentAmount/g,
              depreciationPaymentsText
            );
            content = content.replace(
              /_depreciationPaymentDate_ tarihinde _depreciationPaymentAmount_/g,
              depreciationPaymentsText
            );
            content = content.replace(
              /\*depreciationPaymentDate\* tarihinde \*depreciationPaymentAmount\*/g,
              depreciationPaymentsText
            );
          }

          // Handle recourse payments replacement in titles/results
          if (
            documentData.recoursePayments &&
            Object.keys(documentData.recoursePayments).length > 0
          ) {
            let insuranceCompany = "";
            const recoursePaymentsText = Object.entries(
              documentData.recoursePayments
            )
              .map(([company, dateAmounts]) => {
                const dateEntries = Object.entries(dateAmounts)
                  .map(([date, amounts]) => {
                    const formattedAmounts = amounts
                      .map((amount) => `${amount} TL`)
                      .join(", ");
                    return `${date} tarihinde ${formattedAmounts} tutarında`;
                  })
                  .join(" ve ");
                insuranceCompany = company || insuranceCompany;
                return `${dateEntries} ${company} Sigorta A.Ş'ye`;
              })
              .join(" ve ");

            // Replace recourse payment placeholders in title/result content - new format
            content = content.replace(
              /\*recoursePaymentDate\* tarihinde \*recoursePaymentAmount\* tutarında \*recoursePaymentCompany\* \*Sigorta A\.Ş\*'ye/g,
              recoursePaymentsText
            );
            content = content.replace(
              /_recoursePaymentDate_ tarihinde _recoursePaymentAmount_ tutarında _recoursePaymentCompany_ _Sigorta A\.Ş_'ye/g,
              recoursePaymentsText
            );
            content = content.replace(
              /recoursePaymentDate tarihinde recoursePaymentAmount tutarında recoursePaymentCompany Sigorta A\.Ş'ye/g,
              recoursePaymentsText
            );
            console.log(
              Object.values(documentData.recoursePayments).slice(-1)[0],
              "recourse payments"
            );
            if (insuranceCompany) {
              console.log("replacing sigorta");
              content = content.replace(
                /recoursePaymentCompany/g,
                insuranceCompany
              );
            }
          }
          // Also include all dictionary keys to ensure they get processed even if not in documentData
          if (data.dictionary) {
            Object.keys(data.dictionary).forEach((key) => {
              if (!allReplacementKeys.hasOwnProperty(key)) {
                allReplacementKeys[key] = ""; // Set empty string for dictionary-only keys
              }
            });
          }

          const sortedKeys = Object.keys(allReplacementKeys).sort(
            (a, b) => b.length - a.length
          );
          sortedKeys.forEach((placeholder) => {
            let value = documentData[placeholder] || "";
            if (value && placeholder.toLowerCase().includes("amount")) {
              value = `${value} TL`;
            }
            // Check if dictionary has a translation for this placeholder
            const dictionaryValue = data.dictionary[placeholder];

            // If dictionary has a translation, use it immediately regardless of document processing status
            // Treat empty strings as "no value" too, but also handle non-string values
            const isEmptyValue =
              !value ||
              (typeof value === "string" && value.trim() === "") ||
              (Array.isArray(value) && value.length === 0) ||
              (typeof value === "object" && Object.keys(value).length === 0);

            const displayValue =
              dictionaryValue && isEmptyValue
                ? `[${dictionaryValue}]`
                : value ||
                  (dictionaryValue ? `[${dictionaryValue}]` : placeholder);

            const regexUnderscore = new RegExp(`_${placeholder}_`, "g");
            const regexPlain = new RegExp(placeholder, "g");
            if (regexUnderscore.test(content)) {
              content = content.replace(regexUnderscore, displayValue);
            } else {
              content = content.replace(regexPlain, displayValue);
            }
          });

          return content;
        }
        return "";
      })
      .filter((title) => title.trim() !== "") // Remove empty titles
      .join("\n"); // Add two newlines between titles for spacing

    processedTemplate = processedTemplate.replace(
      new RegExp(`\\[${placeholder}\\]`, "g"),
      titlesContent + (field === "title" ? "\n" : "")
    );
  } else {
    // If no titles after otherwise, remove the placeholder
    processedTemplate = processedTemplate.replace(
      new RegExp(`\\[${placeholder}\\]`, "g"),
      ""
    );
  }

  return processedTemplate;
};

// Define a backup template in case we can't load from Firebase
const BACKUP_TEMPLATE = `Petition Template cannot be loaded. Please contact the administrator.`;

const ResultSummary = ({
  documentData,
  documentOrder,
  onResetData,
  selectedTemplate,
}) => {
  const { allFileUploadsComplete } = useUploadContext();
  const { user } = useAuth0();
  const { requestId } = useRequest();
  const [petitionContent, setPetitionContent] = useState(
    "Şablon yükleniyor..."
  );
  const [isTemplateLoaded, setIsTemplateLoaded] = useState(false);
  const [dictionary, setDictionary] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    type: "pdf", // 'pdf' or 'word'
    message: "",
    buttons: [],
    onConfirm: null,
  });

  // Add pending action state
  const [pendingAction, setPendingAction] = useState(null);

  // Add custom CSS styles for formatting
  useEffect(() => {
    // Create a style element
    const style = document.createElement("style");
    // Add custom CSS for proper list formatting
    style.textContent = `
      .result-summary ol {
        padding-left: 20px;
        margin-top: 0;
        margin-bottom: 0;
      }
      .result-summary li {
        margin-bottom: 0;
        text-indent: 0;
      }
      .result-summary ol li {
        padding-left: 0;
      }
    `;
    // Append the style to the document head
    document.head.appendChild(style);

    // Clean up on component unmount
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const damagePaymentsText = useMemo(() => {
    if (!documentData || Object.keys(documentData).length === 0) {
      return null;
    }

    let text = "";
    if (
      documentData.damagePayments &&
      Object.keys(documentData.damagePayments).length > 0
    ) {
      text = Object.entries(documentData.damagePayments)
        .map(([date, amounts]) => {
          amounts = amounts.map((amount) => `${amount} TL`);
          const formattedAmounts = amounts.join(", ");
          return `*${date}* tarihinde *${formattedAmounts}*`;
        })
        .join(" ve ");
    }
    return text;
  }, [documentData]);

  const thirdPartyPaymentsText = useMemo(() => {
    if (!documentData || Object.keys(documentData).length === 0) {
      return null;
    }

    let text = "";
    if (
      documentData.thirdPartyPayments &&
      Object.keys(documentData.thirdPartyPayments).length > 0
    ) {
      // Group by plate, then by date, then amounts
      text = Object.entries(documentData.thirdPartyPayments)
        .map(([plate, dateAmounts]) => {
          const dateEntries = Object.entries(dateAmounts)
            .map(([date, amounts]) => {
              const formattedAmounts = amounts
                .map((amount) => `${amount} TL`)
                .join(", ");
              return `*${date}* tarihinde *${formattedAmounts}*`;
            })
            .join(" ve ");
          return `*${plate}* plakalı araca ${dateEntries}`;
        })
        .join(" ve ");
    }
    return text;
  }, [documentData]);

  const depreciationPaymentsText = useMemo(() => {
    if (!documentData || Object.keys(documentData).length === 0) {
      return null;
    }

    let text = "";
    if (
      documentData.depreciationPayments &&
      Object.keys(documentData.depreciationPayments).length > 0
    ) {
      text = Object.entries(documentData.depreciationPayments)
        .map(([date, amounts]) => {
          amounts = amounts.map((amount) => `${amount} TL`);
          const formattedAmounts = amounts.join(", ");
          return `*${date}* tarihinde *${formattedAmounts}*`;
        })
        .join(" ve ");
    }
    return text;
  }, [documentData]);

  const recoursePaymentsText = useMemo(() => {
    if (!documentData || Object.keys(documentData).length === 0) {
      return null;
    }

    let text = "";
    if (
      documentData.recoursePayments &&
      Object.keys(documentData.recoursePayments).length > 0
    ) {
      // Group by company, then by date, then amounts
      text = Object.entries(documentData.recoursePayments)
        .map(([company, dateAmounts]) => {
          const dateEntries = Object.entries(dateAmounts)
            .map(([date, amounts]) => {
              const formattedAmounts = amounts
                .map((amount) => `${amount} TL`)
                .join(", ");
              return `*${date}* tarihinde *${formattedAmounts}* tutarında`;
            })
            .join(" ve ");
          return `${dateEntries} *${company}* *Sigorta A.Ş*'ye`;
        })
        .join(" ve ");
    }
    return text;
  }, [documentData]);
  // Function to process template
  const processTemplate = useCallback(
    (data) => {
      const template = data.template;
      // Check if template is available
      if (!template || !documentData) return "";

      console.log("Original template content length:", template.length);

      // Handle optional documents first
      let processedTemplate = template;

      // Process optional documents if we have selectedTemplate
      if (selectedTemplate && selectedTemplate.optionalDocuments) {
        selectedTemplate.optionalDocuments.forEach((optionalDocId) => {
          console.log(`Processing optional document: ${optionalDocId}`);

          // Check if the optional document has been uploaded and processed
          const isDocumentUploaded =
            documentData[optionalDocId] &&
            ((Array.isArray(documentData[optionalDocId]) &&
              documentData[optionalDocId].length > 0 &&
              documentData[optionalDocId].some(
                (doc) => doc.status === "DONE"
              )) ||
              (typeof documentData[optionalDocId] === "object" &&
                documentData[optionalDocId].status === "DONE"));

          // Special handling for damage_receipt - also check if damagePayments has any data
          if (optionalDocId === "damage_receipt") {
            const hasDamagePayments =
              documentData.damagePayments &&
              Object.keys(documentData.damagePayments).length > 0 &&
              Object.values(documentData.damagePayments).some(
                (amounts) => Array.isArray(amounts) && amounts.length > 0
              );

            // Override isDocumentUploaded for damage_receipt based on actual payment data
            const hasValidDamageReceipts =
              isDocumentUploaded || hasDamagePayments;

            // Create regex patterns for start and end markers
            const startPattern = new RegExp(
              `^.*${optionalDocId}_start.*$`,
              "gm"
            );
            const endPattern = new RegExp(`^.*${optionalDocId}_end.*$`, "gm");

            // Find the full content between start and end markers (including the markers)
            // Use a more robust pattern that handles different line endings
            const fullBlockPattern = new RegExp(
              `.*${optionalDocId}_start.*[\\r\\n]+[\\s\\S]*?.*${optionalDocId}_end.*[\\r\\n]*`,
              "g"
            );

            if (hasValidDamageReceipts) {
              // Document is uploaded: remove only the start and end marker lines, keep content
              processedTemplate = processedTemplate.replace(startPattern, "");
              processedTemplate = processedTemplate.replace(endPattern, "");
            } else {
              // Document is not uploaded: remove the entire block including content between markers
              processedTemplate = processedTemplate.replace(
                fullBlockPattern,
                ""
              );
            }
          } else if (optionalDocId === "third_party_receipt") {
            const hasThirdPartyPayments =
              documentData.thirdPartyPayments &&
              Object.values(documentData.thirdPartyPayments).some(
                (amounts) =>
                  Object.keys(amounts).length > 0 &&
                  Object.values(amounts).some(
                    (value) => Array.isArray(value) && value.length > 0
                  )
              );

            // Override isDocumentUploaded for third_party_receipt based on actual payment data
            const hasValidThirdPartyReceipts =
              isDocumentUploaded || hasThirdPartyPayments;

            // Create regex patterns for start and end markers
            const startPattern = new RegExp(
              `^.*${optionalDocId}_start.*$`,
              "gm"
            );
            const endPattern = new RegExp(`^.*${optionalDocId}_end.*$`, "gm");

            // Find the full content between start and end markers (including the markers)
            // Use a more robust pattern that handles different line endings
            const fullBlockPattern = new RegExp(
              `.*${optionalDocId}_start.*[\\r\\n]+[\\s\\S]*?.*${optionalDocId}_end.*[\\r\\n]*`,
              "g"
            );

            if (hasValidThirdPartyReceipts) {
              processedTemplate = processedTemplate.replace(startPattern, "");
              processedTemplate = processedTemplate.replace(endPattern, "");
            } else {
              processedTemplate = processedTemplate.replace(
                fullBlockPattern,
                ""
              );
            }
          } else if (optionalDocId === "recourse_receipt") {
            const hasRecoursePayments =
              documentData.recoursePayments &&
              Object.values(documentData.recoursePayments).some(
                (amounts) =>
                  Object.keys(amounts).length > 0 &&
                  Object.values(amounts).some(
                    (value) => Array.isArray(value) && value.length > 0
                  )
              );

            // Override isDocumentUploaded for recourse_receipt based on actual payment data
            const hasValidRecourseReceipts =
              isDocumentUploaded || hasRecoursePayments;

            // Create regex patterns for start and end markers
            const startPattern = new RegExp(
              `^.*${optionalDocId}_start.*$`,
              "gm"
            );
            const endPattern = new RegExp(`^.*${optionalDocId}_end.*$`, "gm");

            // Find the full content between start and end markers (including the markers)
            // Use a more robust pattern that handles different line endings
            const fullBlockPattern = new RegExp(
              `.*${optionalDocId}_start.*[\\r\\n]+[\\s\\S]*?.*${optionalDocId}_end.*[\\r\\n]*`,
              "g"
            );

            if (hasValidRecourseReceipts) {
              processedTemplate = processedTemplate.replace(startPattern, "");
              processedTemplate = processedTemplate.replace(endPattern, "");
            } else {
              processedTemplate = processedTemplate.replace(
                fullBlockPattern,
                ""
              );
            }
          } else {
            // Create regex patterns for start and end markers
            const startPattern = new RegExp(
              `^.*${optionalDocId}_start.*$`,
              "gm"
            );
            const endPattern = new RegExp(`^.*${optionalDocId}_end.*$`, "gm");

            // Find the full content between start and end markers (including the markers)
            // Use a more robust pattern that handles different line endings
            const fullBlockPattern = new RegExp(
              `.*${optionalDocId}_start.*[\\r\\n]+[\\s\\S]*?.*${optionalDocId}_end.*[\\r\\n]*`,
              "g"
            );

            // Standard handling for other optional documents
            if (isDocumentUploaded) {
              processedTemplate = processedTemplate.replace(startPattern, "");
              processedTemplate = processedTemplate.replace(endPattern, "");
            } else {
              processedTemplate = processedTemplate.replace(
                fullBlockPattern,
                ""
              );
            }
          }
        });
      }

      // Create a mapping of placeholders to their values dynamically from documentData
      const replacements = {};
      documentData.totalPaymentAmount = Object.values(
        documentData.depreciationPayments || {}
      )
        .flat()
        .reduce((acc, curr) => acc + parseAmount(curr), 0);

      documentData.totalPaymentAmount += Object.values(
        documentData.damagePayments || {}
      )
        .flat()
        .reduce((acc, curr) => acc + parseAmount(curr), 0);

      // Handle nested structure for thirdPartyPayments: company -> date -> amounts[]
      if (documentData.thirdPartyPayments) {
        Object.values(documentData.thirdPartyPayments).forEach(
          (dateAmounts) => {
            Object.values(dateAmounts).forEach((amounts) => {
              amounts.forEach((amount) => {
                documentData.totalPaymentAmount += parseAmount(amount);
              });
            });
          }
        );
      }

      // Handle nested structure for recoursePayments: company -> date -> amounts[]
      if (documentData.recoursePayments) {
        Object.values(documentData.recoursePayments).forEach((dateAmounts) => {
          Object.values(dateAmounts).forEach((amounts) => {
            amounts.forEach((amount) => {
              documentData.totalPaymentAmount += parseAmount(amount);
            });
          });
        });
      }

      documentData.totalPaymentAmount =
        documentData.totalPaymentAmount.toLocaleString("en-US");

      Object.keys(documentData).forEach((key) => {
        replacements[key] = documentData[key] || "";
      });

      // Also include all dictionary keys to ensure they get processed even if not in documentData
      if (data.dictionary) {
        Object.keys(data.dictionary).forEach((key) => {
          if (!replacements.hasOwnProperty(key)) {
            replacements[key] = ""; // Set empty string for dictionary-only keys
          }
        });
      }

      // Sort keys by length in descending order to handle longer keys first
      const sortedKeys = Object.keys(replacements).sort(
        (a, b) => b.length - a.length
      );

      // Replace all occurrences of each placeholder with its corresponding value
      console.log(documentOrder, "Selected Document Order");
      if (documentOrder && documentOrder.length > 0) {
        const documentOrderIds = documentOrder.map((o) => o.id);

        if (
          documentOrderIds.includes("MergeRequest") &&
          data.titles &&
          data.titles["MergeRequest"]
        ) {
          processedTemplate = processedTemplate.replace(
            /\[titleMergeRequest\]/g,
            data.titles["MergeRequest"].title || ""
          );
          const resultBeforePlaceholder = "[resultBeforeOtherwise]";
          if (processedTemplate.includes(resultBeforePlaceholder)) {
            const mergeRequestResult = data.titles["MergeRequest"].result || "";
            processedTemplate = processedTemplate.replace(
              resultBeforePlaceholder,
              mergeRequestResult.replace("order", "1") +
                "\n\n" +
                resultBeforePlaceholder
            );
          }
        } else {
          processedTemplate = processedTemplate.replace(
            /\[titleMergeRequest\]/g,
            ""
          );
        }

        data.titles &&
          Object.keys(data.titles).forEach((title) => {
            if (title.includes("SingleTop")) {
              const titlePattern = `title${title}`;
              if (documentOrderIds.some((id) => id.includes(title))) {
                processedTemplate = processedTemplate.replace(
                  new RegExp(`\\[${titlePattern}\\]`, "g"),
                  data.titles[title].title || ""
                );
              } else {
                processedTemplate = processedTemplate.replace(
                  new RegExp(`\\[${titlePattern}\\]`, "g"),
                  ""
                );
              }
            }
          });

        processedTemplate = replaceTitleAndResult(
          processedTemplate,
          documentOrderIds,
          documentOrderIds.filter(
            (id) =>
              data.titles[id].beforeTitleOtherwise &&
              id !== "MergeRequest" &&
              !id.includes("SingleTop")
          ),
          data,
          "titleBeforeOtherwise",
          documentData
        );
        processedTemplate = replaceTitleAndResult(
          processedTemplate,
          documentOrderIds,
          documentOrderIds.filter(
            (id) =>
              !data.titles[id].beforeTitleOtherwise &&
              id !== "MergeRequest" &&
              !id.includes("SingleTop")
          ),
          data,
          "titleAfterOtherwise",
          documentData
        );
        processedTemplate = replaceTitleAndResult(
          processedTemplate,
          documentOrderIds,
          documentOrderIds.filter(
            (id) =>
              data.titles[id].beforeResultOtherwise &&
              id !== "MergeRequest" &&
              !id.includes("SingleTop")
          ),
          data,
          "resultBeforeOtherwise",
          documentData
        );
        processedTemplate = replaceTitleAndResult(
          processedTemplate,
          documentOrderIds,
          documentOrderIds.filter(
            (id) =>
              !data.titles[id].beforeResultOtherwise &&
              id !== "MergeRequest" &&
              !id.includes("SingleTop")
          ),
          data,
          "resultAfterOtherwise",
          documentData
        );
        processedTemplate = replaceRemainingOrders(
          processedTemplate,
          documentOrderIds.filter(
            (id) => id !== "MergeRequest" && !id.includes("SingleTop")
          ).length
        );
      }
      console.log(data.dictionary, "Dictionary");

      // Handle damage payments replacement BEFORE dictionary mapping
      if (damagePaymentsText) {
        console.log(
          "trying damage payment replacement before dictionary mapping"
        );
        // Old format replacements
        processedTemplate = processedTemplate.replace(
          /damagePaymentDate tarihinde damagePaymentAmount/g,
          damagePaymentsText
        );
        processedTemplate = processedTemplate.replace(
          /_damagePaymentDate_ tarihinde _damagePaymentAmount_/g,
          damagePaymentsText
        );
        processedTemplate = processedTemplate.replace(
          /\*damagePaymentDate\* tarihinde \*damagePaymentAmount\*/g,
          damagePaymentsText
        );
      }

      // Handle third party payments replacement BEFORE dictionary mapping
      if (thirdPartyPaymentsText) {
        console.log(
          "trying third party payment replacement before dictionary mapping"
        );
        // New format replacements
        processedTemplate = processedTemplate.replace(
          /\*thirdPartyPlate\* plakalı araca \*thirdPartyPaymentDate\* tarihinde \*thirdPartyPaymentAmount\*/g,
          thirdPartyPaymentsText
        );
        processedTemplate = processedTemplate.replace(
          /_thirdPartyPlate_ plakalı araca _thirdPartyPaymentDate_ tarihinde _thirdPartyPaymentAmount_/g,
          thirdPartyPaymentsText
        );
        processedTemplate = processedTemplate.replace(
          /thirdPartyPlate plakalı araca thirdPartyPaymentDate tarihinde thirdPartyPaymentAmount/g,
          thirdPartyPaymentsText
        );

        // Old format replacements for backward compatibility
        processedTemplate = processedTemplate.replace(
          /thirdPartyPaymentDate tarihinde thirdPartyPaymentAmount/g,
          thirdPartyPaymentsText
        );
        processedTemplate = processedTemplate.replace(
          /_thirdPartyPaymentDate_ tarihinde _thirdPartyPaymentAmount_/g,
          thirdPartyPaymentsText
        );
        processedTemplate = processedTemplate.replace(
          /\*thirdPartyPaymentDate\* tarihinde \*thirdPartyPaymentAmount\*/g,
          thirdPartyPaymentsText
        );
      }

      // Handle depreciation payments replacement BEFORE dictionary mapping
      if (depreciationPaymentsText) {
        console.log(
          "trying depreciation payment replacement before dictionary mapping"
        );
        // Old format replacements
        processedTemplate = processedTemplate.replace(
          /depreciationPaymentDate tarihinde depreciationPaymentAmount/g,
          depreciationPaymentsText
        );
        processedTemplate = processedTemplate.replace(
          /_depreciationPaymentDate_ tarihinde _depreciationPaymentAmount_/g,
          depreciationPaymentsText
        );
        processedTemplate = processedTemplate.replace(
          /\*depreciationPaymentDate\* tarihinde \*depreciationPaymentAmount\*/g,
          depreciationPaymentsText
        );
      }

      // Handle recourse payments replacement BEFORE dictionary mapping
      if (recoursePaymentsText) {
        console.log(
          "trying recourse payment replacement before dictionary mapping"
        );
        // New format replacements
        processedTemplate = processedTemplate.replace(
          /\*recoursePaymentDate\* tarihinde \*recoursePaymentAmount\* tutarında \*recoursePaymentCompany\* \*Sigorta A\.Ş\*'ye/g,
          recoursePaymentsText
        );
        processedTemplate = processedTemplate.replace(
          /_recoursePaymentDate_ tarihinde _recoursePaymentAmount_ tutarında _recoursePaymentCompany_ _Sigorta A\.Ş_'ye/g,
          recoursePaymentsText
        );
        processedTemplate = processedTemplate.replace(
          /recoursePaymentDate tarihinde recoursePaymentAmount tutarında recoursePaymentCompany Sigorta A\.Ş'ye/g,
          recoursePaymentsText
        );
      }
      // Use sorted keys for replacement
      sortedKeys.forEach((placeholder) => {
        let value = replacements[placeholder];

        if (placeholder.toLowerCase().includes("amount") && value) {
          value = `${value} TL`;
        }
        // Check if dictionary has a translation for this placeholder
        const dictionaryValue = data.dictionary[placeholder];

        // If dictionary has a translation, use it immediately regardless of document processing status
        // Treat empty strings as "no value" too, but also handle non-string values
        const isEmptyValue =
          !value ||
          (typeof value === "string" && value.trim() === "") ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === "object" && Object.keys(value).length === 0);

        const displayValue =
          dictionaryValue && isEmptyValue
            ? `[${dictionaryValue}]`
            : value || (dictionaryValue ? `[${dictionaryValue}]` : placeholder);

        // Prefer replacing the underscored version if it exists in the template
        const regexUnderscore = new RegExp(`_${placeholder}_`, "g");
        const regexPlain = new RegExp(placeholder, "g");
        if (regexUnderscore.test(processedTemplate)) {
          processedTemplate = processedTemplate.replace(
            regexUnderscore,
            displayValue
          );
        } else {
          processedTemplate = processedTemplate.replace(
            regexPlain,
            displayValue
          );
        }
      });

      console.log("Final template content length:", processedTemplate.length);

      return processedTemplate;
    },
    [
      documentData,
      documentOrder,
      selectedTemplate,
      damagePaymentsText,
      thirdPartyPaymentsText,
      depreciationPaymentsText,
      recoursePaymentsText,
    ]
  );

  // Fetch template and process data
  useEffect(() => {
    // Load template immediately when uploads are complete, regardless of document data
    if (!allFileUploadsComplete) return;

    const fetchTemplate = async () => {
      try {
        if (!documentData || !documentData.documentType) {
          // If no document type yet, we can't load template
          return;
        }

        // Get template name based on document type
        const templateName = documentData.documentType;

        // Use Firestore to get the template (client-scoped: templates/{clientId}/templates)
        const firestore = getFirestore();
        const clientId = getClientId();
        const templateDocRef = doc(
          firestore,
          "templates",
          clientId,
          "templates",
          templateName
        );

        try {
          const templateDoc = await getDoc(templateDocRef);

          if (!templateDoc.exists()) {
            throw new Error(`Template not found: ${templateName}`);
          }

          const templateData = templateDoc.data();
          setDictionary(templateData.dictionary || null);
          const processedTemplate = processTemplate(templateData);

          // Ensure proper newlines in critical places
          const formattedTemplate = ensureProperNewlines(processedTemplate);

          // Fix indentation issues with numbered lists (early processing)
          const fixedTemplate = formattedTemplate
            .replace(/^[ \t]+(\d+\.\s+)/gm, "$1")
            .replace(/\n[ \t]+(\d+\.\s+)/g, "\n$1");

          // Convert single asterisks to double asterisks for proper bold formatting in ReactMarkdown
          const correctedTemplate = formatBoldText(fixedTemplate);

          console.log("Template content length:", correctedTemplate.length);

          setPetitionContent(correctedTemplate);
          setIsTemplateLoaded(true);
        } catch (error) {
          console.error(
            `Error loading template ${templateName} from Firestore:`,
            error
          );
          console.log("Using backup template instead");

          // Use the backup template if Firestore fails
          const processedTemplate = processTemplate(BACKUP_TEMPLATE);
          const formattedTemplate = ensureProperNewlines(processedTemplate);
          const correctedTemplate = formatBoldText(formattedTemplate);

          setPetitionContent(correctedTemplate);
          setIsTemplateLoaded(true);
        }
      } catch (error) {
        console.error("Template processing error:", error);
        setPetitionContent(
          `Şablon işlenirken bir hata oluştu: ${error.message}. Lütfen yönetici ile iletişime geçiniz.`
        );
      }
    };

    fetchTemplate();
  }, [
    allFileUploadsComplete, // Load template when uploads complete
    documentData,
    documentOrder,
    selectedTemplate, // Re-process when template changes
    damagePaymentsText, // Re-process when damage payments change
    thirdPartyPaymentsText, // Re-process when third party payments change
    depreciationPaymentsText, // Re-process when depreciation payments change
    recoursePaymentsText, // Re-process when recourse payments change
    processTemplate, // Function dependency
  ]);

  // Modal component
  const PlaceholderModal = () => {
    if (!showModal) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-body">
            <p>{modalConfig.message}</p>
          </div>
          <div className="modal-buttons">
            {modalConfig.buttons.map((button, index) => (
              <button
                key={index}
                className={`modal-btn ${button.type}`}
                onClick={button.onClick}
              >
                {button.text}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Function to check for placeholders in the content wrapper
  const hasPlaceholdersWrapper = () => {
    return hasPlaceholders(petitionContent);
  };

  // PDF oluşturma fonksiyonu - now using engine
  const generatePDF = async () => {
    const result = await generatePDFEngine(
      petitionContent,
      hasPlaceholdersWrapper,
      (config) => {
        setModalConfig({
          ...config,
          buttons: config.buttons.map((button) => ({
            ...button,
            onClick: () => {
              setShowModal(false);
              if (button.text === "Word İndir") {
                setPendingAction("docx");
              } else {
                setPendingAction(null);
              }
            },
          })),
        });
      },
      setShowModal
    );

    if (result.shouldProceed) {
      await proceedToPDFGeneration(
        petitionContent,
        documentData,
        selectedTemplate?.id,
        dictionary,
        user?.email,
        requestId,
        documentOrder
      );
    }
  };

  // DOCX oluşturma fonksiyonu - now using engine
  const generateDOCX = async () => {
    const result = await generateDOCXEngine(
      petitionContent,
      hasPlaceholdersWrapper,
      (config) => {
        setModalConfig({
          ...config,
          buttons: config.buttons.map((button) => ({
            ...button,
            onClick: () => {
              setShowModal(false);
              if (button.text === "Evet") {
                setPendingAction("docx");
              } else {
                setPendingAction(null);
              }
            },
          })),
        });
      },
      setShowModal
    );

    if (result.shouldProceed) {
      await proceedWithDOCXGeneration(
        petitionContent,
        documentData,
        selectedTemplate?.id,
        dictionary,
        user?.email,
        requestId,
        documentOrder
      );
    }
  };

  // Handle pending actions after modal is closed
  useEffect(() => {
    if (pendingAction === "docx") {
      proceedWithDOCXGeneration(
        petitionContent,
        documentData,
        selectedTemplate?.id,
        dictionary,
        user?.email,
        requestId,
        documentOrder
      );
      setPendingAction(null);
    }
  }, [
    pendingAction,
    petitionContent,
    documentData,
    selectedTemplate,
    dictionary,
    user?.email,
    requestId,
    documentOrder,
  ]);

  // Add a refresh button function to the ResultSummary component
  const handleRefresh = () => {
    // Call the reset function passed from App.js
    if (onResetData) {
      onResetData();
    }
    // Reset local state in ResultSummary
    setPetitionContent("Şablon yükleniyor...");
    setIsTemplateLoaded(false);
  };

  // -------------------------------
  // NEW: determine if content is ready to be displayed
  const isContentReady =
    isTemplateLoaded &&
    petitionContent &&
    petitionContent.trim() !== "" &&
    petitionContent.trim() !== "Şablon yükleniyor...";

  // Conditional rendering based on state
  if (!allFileUploadsComplete || !isContentReady) {
    console.log("ResultSummary: content not ready, showing loading animation");
    return (
      <div className="result-summary-info">
        <Lottie
          animationData={waitingAnimation}
          style={{ width: 400, height: 600 }}
        />
      </div>
    );
  }

  console.log("ResultSummary: content ready, showing summary content");
  return (
    <div className="result-summary-container">
      <div className="result-summary">
        <ReactMarkdown
          rehypePlugins={[rehypeRaw]}
          remarkPlugins={[]}
          breaks={true}
          components={{
            ol: ({ node, ordered, ...props }) => (
              <ol style={{ paddingLeft: "20px" }} {...props} />
            ),
            li: ({ node, ...props }) => (
              <li style={{ marginBottom: "0" }} {...props} />
            ),
          }}
        >
          {petitionContent
            .replace(/\r\n/g, "\n")
            .replace(/\[\[TAB_INDENT\]\]/g, "")
            .replace(/(_[^_]+_:[^\n]+)\n/g, "$1<br/>\n")
            // Remove all tabs and leading 4+ spaces to prevent markdown code block detection
            .replace(/^\t+/gm, "")
            .replace(/^[ ]{4,}/gm, "")}
        </ReactMarkdown>
        <PlaceholderModal />
      </div>
      <div className="summary-actions">
        <button
          id="download-summary-btn"
          className="download-summary-btn"
          onClick={generatePDF}
          disabled={!isTemplateLoaded}
        >
          PDF İndir
        </button>
        <button
          id="download-word-btn"
          className="download-word-btn"
          onClick={generateDOCX}
          disabled={!isTemplateLoaded}
        >
          Word İndir
        </button>
        <button
          className="refresh-btn"
          onClick={handleRefresh}
          aria-label="Sayfayı yenile"
        >
          Yenile
        </button>
      </div>
    </div>
  );
};

export default ResultSummary;
